import { createContext, useContext, useEffect, useState } from 'react'

type Theme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider')
  }
  return context
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>('light')

  // Initialize theme from localStorage
  useEffect(() => {
    // Migrate from old localStorage key
    const oldStored = localStorage.getItem('unirouter-theme')
    if (oldStored) {
      localStorage.setItem('optra-ai-theme', oldStored)
      localStorage.removeItem('unirouter-theme')
    }
    const stored = localStorage.getItem('optra-ai-theme') as Theme | null
    if (stored && ['light', 'dark'].includes(stored)) {
      setThemeState(stored)
    }
  }, [])

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement

    // Remove both classes first
    root.classList.remove('light', 'dark')
    // Add the current theme class
    root.classList.add(theme)

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]')
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        'content',
        theme === 'dark' ? '#020617' : '#ffffff' // slate-950 for dark
      )
    }
  }, [theme])

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme)
    localStorage.setItem('optra-ai-theme', newTheme)
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}
